import {Component, OnDestroy, OnInit} from '@angular/core';
import {Update} from '@ngrx/entity';
import {Observable, Subscription, combineLatest} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {SubheaderService} from '../../../../core/_base/layout';
import {AppState} from '../../../../core/reducers';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {delay} from 'rxjs/operators';
import {PurposeOfValuationUpdated} from '../../../../core/linked-tables';
import { TranslateService } from '@ngx-translate/core';

import {
    PurposeOfValuation,
    PurposeOfValuationOnServerCreated,
    selectPurposeOfValuationsActionLoading,
    selectPurposeOfValuationById,
    selectLastCreatedPurposeOfValuationId
} from '../../../../core/linked-tables';
import {Location} from '@angular/common';

@Component({
    selector: 'kt-purpose-of-valuation-edit',
    templateUrl: './purpose-of-valuation-edit.component.html',
    styleUrls: ['./purpose-of-valuation-edit.component.scss']
})


export class PurposeOfValuationEditComponent implements OnInit, OnDestroy {

    model: PurposeOfValuation;
    oldModel: PurposeOfValuation;

    purposeOfValuationForm: UntypedFormGroup;
    hasFormErrors = false;

    loading$: Observable<boolean>;
    viewLoading = false;
    loadingAfterSubmit = false;
    mode = 'NEW';
    // Private properties
    private componentSubscriptions: Subscription;
    private subscriptions: Subscription[] = [];
    readonly = false

    /**
     * Component constructor
     *
     * @param subheaderService: SubheaderService
     * @param activatedRoute: ActivatedRoute
     * @param router: Router
     * @param fb: FormBuilder
     * @param location: Location
     * @param store: Store<AppState>
     */
    constructor(public subheaderService: SubheaderService,
                private activatedRoute: ActivatedRoute,
                private router: Router,
                private fb: UntypedFormBuilder,
                private location: Location,
                private store: Store<AppState>,
                private translate: TranslateService) {
    }

    ngOnInit() {
        this.loading$ = this.store.pipe(select(selectPurposeOfValuationsActionLoading));
        const routeSubscription = combineLatest([ 
            this.activatedRoute.params,
            this.activatedRoute.data
        ]).subscribe(([params, data]) => {
            const id = params.id;
            this.readonly = data.readonly
            if (id && id > 0) {
                this.store.pipe(select(selectPurposeOfValuationById(id))).subscribe(res => {
                    if (res) {
                        this.oldModel = Object.assign({}, res);
                        this.model = Object.assign({}, this.oldModel);
                        this.initClient();
                    }
                });
            } else {
                this.model = new PurposeOfValuation();
                this.model.clear();
                this.oldModel = Object.assign({}, this.model);
                this.initClient();
            }
        });
        this.subscriptions.push(routeSubscription);

        if (this.activatedRoute.snapshot.paramMap.get('id') != null) {
            this.setBreadCrumbs(Number(this.activatedRoute.snapshot.paramMap.get('id')));
        } else {
            this.setBreadCrumbs(0);
        }
    }

    setBreadCrumbs(id: number): void {
        this.subheaderService.setTitle(this.getComponentTitle());
        this.subheaderService.setBreadcrumbs([
            {title: 'Developer', page: `../admin-management`},
            {title: 'Linked tables', page: `../admin-management/linked-tables`},
            {title: 'Purpose Of Valuations', page: `../admin-management/linked-tables/purpose-of-valuations`},
            {
                title: this.getComponentTitle(),
                page: id > 0 ? `../admin-management/linked-tables/purpose-of-valuations/edit/${id}`
                    : `../admin-management/linked-tables/purpose-of-valuations/add`
            }
        ]);
    }

    ngOnDestroy(): void {
        if (this.componentSubscriptions) {
            this.componentSubscriptions.unsubscribe();
        }
    }

    initClient() {
        this.createForm();
        // set subheaderService
    }


    createForm() {
        this.purposeOfValuationForm = this.fb.group({
            name: [this.model.name, Validators.required],
        });
    }

    /**
     * Returns page title
     */
    getComponentTitle(): string {
        if (this.model && this.model.id > 0) {
            this.mode = 'EDIT';
            return this.translate.instant('PURPOSE_OF_VALUATIONS.FORM.TITLE.EDIT', {name: this.model.name});
        }
        return this.translate.instant('PURPOSE_OF_VALUATIONS.FORM.TITLE.NEW');
    }

    /**
     * Check control is invalid
     * @param controlName: string
     */
    isControlInvalid(controlName: string): boolean {
        const control = this.purposeOfValuationForm.controls[controlName];
        return control.invalid && control.touched;
    }

    reset() {
        this.model = Object.assign({}, this.oldModel);
        this.createForm();
        this.hasFormErrors = false;
        this.purposeOfValuationForm.markAsPristine();
        this.purposeOfValuationForm.markAsUntouched();
        this.purposeOfValuationForm.updateValueAndValidity();
    }

    /** ACTIONS */

    /**
     * On Submit
     */
    onSubmit(withBack: boolean = false) {
        this.hasFormErrors = false;
        const controls = this.purposeOfValuationForm.controls;
        /** check form */
        if (this.purposeOfValuationForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );

            this.hasFormErrors = true;
            return;
        }

        if (this.model.id > 0) {
            this.updateClient(this.model);
        } else {
            this.createClient(this.model);
        }
    }

    /**
     * Update model
     *
     * @param _model: PurposeOfValuationModel
     */
    updateClient(_model: PurposeOfValuation) {
        this.loadingAfterSubmit = true;
        this.viewLoading = true;

        const updateClient: Update<PurposeOfValuation> = {
            id: _model.id,
            changes: _model
        };
        this.store.dispatch(new PurposeOfValuationUpdated({
            partialpurposeOfValuation: updateClient,
            purposeOfValuation: _model
        }));
        this.oldModel = _model;

        this.navigateToParent();

    }

    /**
     * Create model
     *
     * @param _model: PurposeOfValuationModel
     */
    createClient(_model: PurposeOfValuation) {
        this.store.dispatch(new PurposeOfValuationOnServerCreated({purposeOfValuation: _model}));
        this.oldModel = _model;
        this.componentSubscriptions = this.store.pipe(
            select(selectLastCreatedPurposeOfValuationId),
            delay(1000), // Remove this line
        ).subscribe(res => {
            if (!res) {
                return;
            }
            this.navigateToParent();
        });
    }

    /** Alect Close event */
    onAlertClose($event) {
        this.hasFormErrors = false;
    }


    isFormValid() {
        return (this.model && this.model.name && this.model.name.length > 0);
    }

    navigateToParent() {
        let url = '../';
        if (this.model.id > 0) {
            url = '../../';
        }
        this.router.navigate([url], {relativeTo: this.activatedRoute});
    }

    canDeactivate() {
        if (this.discard()) {
            if (window.confirm(this.translate.instant('GENERAL.MESSAGE.LOST_CHANGES'))) {
                return true;
            } else {
                // ---------work around angular bug--------- reference: https://github.com/angular/angular/issues/13586
                const currentUrlTree = this.router.createUrlTree([], this.activatedRoute.snapshot);
                const currentUrl = currentUrlTree.toString();
                this.location.go(currentUrl);
                // ---------work around end-----------------
                return false;
            }
        }
        return true;
    }

    discard() {
        if (this.model && this.oldModel) {
            return this.model.name != this.oldModel.name;
        }
        return false;
    }
}
